import ArrowDropDownIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowDropUpIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider, FormControlLabel,
  TextField,
  Typography
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { isPast } from "date-fns";
import debounce from "lodash/debounce";
import { useConfirm } from "material-ui-confirm";
import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CheckboxPopover from "../../../../../design/checkbox-popover";
import LoadingButton from "../../../../../design/wilya-loading-button";
import {
  getHourMinuteDifference,
  hasPermission,
} from "../../../../../utils/helpers";
import { StoreType } from "../../../../../utils/types";
import {
  fetchWorkerCallOffs,
  resetUpdateWorkerAvailabilityStatus,
  updateWorkerAvailabilityStatus
} from "../../../../full-time-worker-scheduler/store/worker-scheduler-slice";
import {
  getFullTimeShiftsMeta,
  reassignWorkers,
  resetFullTimeShiftViewDetails,
  resetReassignWorkers
} from "../../../store/full-time-shifts-slice";
import {
  getFullTimeShiftViewDetails,
} from "../../../store/full-time-shifts-slice";
import {
  FullTimeShiftRootState,
  Worker,
} from "../../../store/full-time-shifts-type";
import { FILTER_STATUS_ORDER, NON_EFFECTIVE_STATUS_CODES } from "../constants";
import { JobRow } from "./job-row";
import { JobRowSkeleton } from "./job-skeleton";
import MarkCalledOff from "./mark-called-off";
import StatusChangePopover from "./status-change-popover";
import { useResizeObserver } from "./use-resize-observer";
import { WorkerPreviewBox } from "./worker-preview-box";

const REMOVE_COF_POPOVER = "RCOF";

const DEBOUNCE_DELAY = 300;

type Job = {
  id: number;
  jobName: string;
  demand: number;
  workers: Worker[];
  filteredWorkers?: Worker[];
  jobStatus: "OS" | "US" | ""; // Overstaffed, Understaffed, or Just Right
  value: number;
  searchString: string;
};

type SchedulingStatus = {
  id: number;
  label: string;
  checked: boolean;
  code: string;
}

type ActivePopoverProps = {
  type: string;
  data?: Worker;
}

const generateJobSearchString = (jobName: string, workers: Worker[], jobStatus: string) => {
  const workerNames = workers.map((w) => w.name).join(" ");
  const workerInitials = workers.map((w) => w.initials).join(" ");
  const workerStatuses = workers.map((w) => w.status.label).join(" ");
  const workerNotes = workers.map((w) => w.note).join(" ");
  const workerInfos = workers.map((w) => w.info).join(" ");
  const jobStatusString = jobStatus === "US" ?
    "Understaffed" : jobStatus === "OS" ? "Overstaffed" : "At Capacity";
  return `${jobName} ${jobStatusString} ${workerNames} ${workerInitials} ${workerStatuses} ${workerNotes} ${workerInfos}`.toLowerCase();
};

const recalculate = (job: Job) => {
  const demand = job.demand;
  const activeWorkersCount = job.workers.filter(
    (w: Worker) => !NON_EFFECTIVE_STATUS_CODES.includes(w.status.code)
  ).length;

  const diff = activeWorkersCount - demand;

  let status = "";
  if (diff > 0) status = "OS"; // Overstaffed
  else if (diff < 0) status = "US"; // Understaffed

  return {
    ...job,
    value: Math.abs(diff),
    jobStatus: status as "" | "OS" | "US",
    searchString: generateJobSearchString(job.jobName, job.workers, status)
  };
};

export const ShiftDialog = ({
                              openDialog,
                              closeDialog,
                              shiftPatternName,
                              start,
                              end,
                              availableWorkers,
                              requiredWorkers,
                              shiftId,
                              workcenterId,
                              locationId,
                              workcenterName,
                              locationName,
                              jobsMap,
                              workcentersMap,
                              shiftDetails
                            }: {
  openDialog: boolean;
  closeDialog: () => void;
  shiftPatternName: string;
  start: string;
  end: string;
  availableWorkers: number;
  requiredWorkers: number;
  shiftId: string;
  workcenterId: number;
  locationId: number;
  workcenterName: string;
  locationName: string;
  jobsMap: Record<number, string> | undefined;
  workcentersMap: Record<number, string> | undefined;
  shiftDetails: any;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const currentUserId = useSelector((state: any) => state.user?.userData?.id);
  const getFullTimeShiftsMetaStatus = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getFullTimeShiftsMeta.status
  );
  const getFullTimeShiftViewDetailsStatus = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getFullTimeShiftViewDetails.status
  );
  const getFullTimeShiftViewDetailsErrorMessage = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getFullTimeShiftViewDetails.errorMessage
  );
  const fullTimeShiftViewDetails = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.fullTimeShiftViewDetails
  );
  const getFullTimeShiftsFiltersStatus = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getFullTimeShiftsFilters.status
  );
  const schedulingStatuses = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.filterData?.schedulingStatuses
  );
  const workerTypes = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.filterData?.workerTypes
  );
  const workerSubTypes = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.filterData?.workerSubTypes
  );
  const requestTypes = useSelector(
    (state: StoreType) => state.workerScheduler?.requestTypes
  );
  const getRequestStatusTypesStatus = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getRequestStatusTypes.status
  );
  const requestStatusTypes = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.getRequestStatusTypes.requestStatusTypes
  );
  const reassignWorkersStatus = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.reassignWorkers.status
  );
  const reassignWorkersErrorMessage = useSelector(
    (state: FullTimeShiftRootState) => state.fullTimeShifts?.reassignWorkers.errorMessage
  );
  const firstName = useSelector(
    (state: StoreType) => state.user.userData?.firstName
  );
  const lastName = useSelector(
    (state: StoreType) => state.user.userData?.lastName
  );
  const updateWorkerAvailabilityAPIStatus = useSelector(
    (state: StoreType) => state.workerScheduler.updateWorkerAvailabilityStatus.status
  );
  const updateWorkerAvailabilityAPIErrorMessage = useSelector(
    (state: StoreType) => state.workerScheduler.updateWorkerAvailabilityStatus.errorMessage
  );
  const userRolesWithPermission = useSelector((state: any) => state.user.userData.userRolesWithPermissions);
  const yourName = `${firstName} ${lastName}`.trim() || t("full-time-shifts_you");
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [search, setSearch] = useState("");
  const [sortModel, setSortModel] = useState<{
    field: string | null;
    direction: "asc" | "desc" | null;
  }>({ field: "jobName", direction: "asc" });
  const [pinnedWorker, setPinnedWorker] = useState<Worker | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [activePopover, setActivePopover] = useState<ActivePopoverProps | null>(null);
  const [calledOffData, setCalledOffData] = useState<{
    ftRequestTypeId: number;
    ftLeaveTypes: {
      leaveTypeId: number;
      requestedHours: number;
    }[];
    ftSupervisorComment: string;
    ftStartDateTime: string;
    ftEndDateTime: string;
    ftFromStatus: string;
  } | null>(null);
  const [doNotRemindMeTrainingStatusChange, setDoNotRemindMeTrainingStatusChange] = useState(false);
  const [doNotRemindMeRestrictedDutyStatusChange, setDoNotRemindMeRestrictedDutyStatusChange] = useState(false);

  useEffect(() => {
    if (currentUserId) {
      setDoNotRemindMeTrainingStatusChange(
        localStorage.getItem(`doNotRemindMeTrainingStatusChange-${currentUserId}`) === "true"
      );
      setDoNotRemindMeRestrictedDutyStatusChange(
        localStorage.getItem(`doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`) === "true"
      );
    }
  }, [currentUserId]);

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [statusOptions, setStatusOptions] = useState<SchedulingStatus[]>([]);

  const [workerBeingUpdated, setWorkerBeingUpdated] = useState<Worker | null>(null);

  const [workerToDrop, setWorkerToDrop] = useState<Worker | null>(null);
  const [workerToJobId, setWorkerToJobId] = useState<number | null>(null);
  const [lastMovedWorker, setLastMovedWorker] = useState<Worker | null>(null);

  const jobContainerRef = useRef<HTMLDivElement | null>(null);
  const jobRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const setJobRef = (jobId: number, node: HTMLDivElement | null) => {
    if (node) {
      jobRefs.current[jobId] = node;
    }
  };
  const [lastWorkerDroppedToJobId, setLastWorkerDroppedToJobId] = useState<number | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [dialogSize, setDialogSize] = useState({ width: 0, height: 0 });

  const hasPermissionToReassignWorkers = useMemo(() => {
    return hasPermission(userRolesWithPermission, "FULL_TIME_SCHEDULER.REASSIGN_WORKERS") && !isPast(new Date(shiftDetails.endDateTime));
  }, [userRolesWithPermission, shiftDetails]);

  const handlePinWorker = (worker: Worker, e: React.MouseEvent) => {
    if (!NON_EFFECTIVE_STATUS_CODES.includes(worker.status.code) && hasPermissionToReassignWorkers) {
      setMousePosition({ x: e.clientX, y: e.clientY }); // <-- set immediately
      setPinnedWorker(worker);
      setWorkerToDrop(worker);
    }
  };

  useEffect(() => {
    try {
      if (lastWorkerDroppedToJobId && jobContainerRef?.current) {
        const jobElement = jobRefs.current[lastWorkerDroppedToJobId];
        if (jobElement) {
          setTimeout(() => {
            const jobContainer = jobContainerRef.current;
            if (jobElement && jobContainer) {
              const containerTop = jobContainer.getBoundingClientRect().top;
              const jobTop = jobElement.getBoundingClientRect().top;
              const selectedStatusOptionsCodes = statusOptions?.filter(
                statusOption => statusOption.checked
              ).map(
                statusOption => statusOption.code
              );
              const numberOfWorkersInJob = filteredJobs.find(
                job => job.id === lastWorkerDroppedToJobId
              )?.workers?.filter(
                worker => selectedStatusOptionsCodes.includes(worker.status.code)
              )?.length ?? 0;

              const offsetInContainer = jobTop - containerTop;

              const offset = numberOfWorkersInJob > 2 ? (numberOfWorkersInJob - 2) * 66 : 0;

              jobContainer.scrollTo({
                top: jobContainer.scrollTop + offsetInContainer + offset,
                behavior: "smooth"
              });
              setTimeout(() => {
                setLastMovedWorker(null);
              },1000);
            }
          }, 600);
        }
        setLastWorkerDroppedToJobId(null);
      }
    } catch (e) {
      // Do nothing
    }
  }, [filteredJobs, lastWorkerDroppedToJobId, statusOptions]);

  useEffect(() => {
    const generateNote = (
      ftRequestTypeId?: number,
      ftStartDateTime?: string,
      ftEndDateTime?: string
    ): string => {
      if (!ftRequestTypeId || !ftStartDateTime || !ftEndDateTime) return "";
      const requestType = requestTypes?.find((type) => type.id === ftRequestTypeId);
      const durationInMinutes = getHourMinuteDifference(new Date(ftEndDateTime), new Date(ftStartDateTime)).differenceInText;
      if (requestType?.code === "sc") return "";
      return `${requestType?.name ?? ""} (${durationInMinutes})`;
    };
    if (updateWorkerAvailabilityAPIStatus === "fulfilled") {

      // Handle special case of RCOF - Remove Called Off set to SCH as new Status
      const newStatusCode = activePopover?.type === "RCOF" ? "SCH" : activePopover?.type;

      const newStatus = statusOptions.find(statusOption => statusOption.code === newStatusCode);
      if (newStatusCode === "SCH") {
        dispatch(getFullTimeShiftViewDetails({
          shiftId,
          workcenterId
        }));
      } else {
        setJobs(prevState => {
          return prevState.map(job => {
            const updatedWorkers = job.workers.map(worker => {
              if (worker.id === workerBeingUpdated?.id) {
                return {
                  ...worker,
                  status: newStatus ??
                    {
                      id: 1,
                      label: "Scheduled",
                      code: "SCH"
                    } ?? worker.status,
                  note: newStatus?.code === "SCH" ? "" :
                    newStatus?.code === "COF" ?
                      generateNote(
                        calledOffData?.ftRequestTypeId,
                        calledOffData?.ftStartDateTime,
                        calledOffData?.ftEndDateTime
                      )
                      : t("full-time-shifts_by-name", { name: yourName })
                };
              }
              return worker;
            });
            return recalculate({
              ...job,
              workers: updatedWorkers
            });
          });
        });
      }
    }
  }, [
    activePopover,
    calledOffData?.ftEndDateTime,
    calledOffData?.ftRequestTypeId,
    calledOffData?.ftStartDateTime,
    dispatch,
    requestTypes,
    shiftId,
    statusOptions,
    updateWorkerAvailabilityAPIStatus,
    workcenterId,
    workerBeingUpdated,
    yourName,
    t
  ]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    if (pinnedWorker) {
      window.addEventListener("mousemove", handleMouseMove);
    } else {
      setMousePosition({ x: 0, y: 0 });
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [pinnedWorker]);

  const renderSortIcon = (field: string) => {
    if (sortModel.field !== field) return <ArrowDropUpIcon fontSize="small" sx={{
      opacity: 0.5,
      color: "rgba(0,0,0,0.6)"
    }} />;
    if (sortModel.direction === "asc") return <ArrowDropUpIcon fontSize="small" sx={{
      color: "rgba(0,0,0,0.6)"
    }} />;
    if (sortModel.direction === "desc") return <ArrowDropDownIcon fontSize="small" sx={{
      color: "rgba(0,0,0,0.6)"
    }} />;
    return <ArrowDropUpIcon fontSize="small" sx={{
      opacity: 0.5,
      color: "rgba(0,0,0,0.6)"
    }} />;
  };

  const getAvailableStatusOptions = (currentCode: string, isMoved: boolean, originalStatus: string) => {
    const allStatusOptions = [...statusOptions];

    if (isMoved) {
      if (currentCode === "RAS" && originalStatus !== "RAS") {
        // If the worker has been moved, we can only show the "Scheduled" status option
        const scheduledOption = allStatusOptions.find(status => status.code === "SCH");
        return [
          {
            id: scheduledOption?.id ?? 1,
            label: t("full-time-shifts_status-reset", { label: scheduledOption?.label }),
            action: (worker: Worker) => {
              const fromExistingJob = jobs.find((j) =>
                worker.originalJobId === j.id
              );
              moveWorker(worker,
                fromExistingJob && worker.workcenterId === workcenterId ?
                worker.originalJobId ?? -1
                : worker.movedFromJobId ?? -1);
            }
          }
        ];
      } else if (currentCode === "SCH" || originalStatus === "RAS") {
        // If the worker has been moved but is currently "Scheduled",
        // we can show the "Reassigned" status option
        const reassignedOption = allStatusOptions.find(status => status.code === "RAS");
        return [
          {
            id: reassignedOption?.id ?? 3,
            label: t("full-time-shifts_status-reset", { label: reassignedOption?.label }),
            action: (worker: Worker) => {
              moveWorker(worker, worker.movedFromJobId ?? -1);
            }
          }
        ];
      }
    }

    // Special case: if the worker is currently "Called Off"
    if (currentCode === "COF") {
      const removeCofOption = allStatusOptions.find(status => status.code === "COF");
      return [
        {
          id: removeCofOption?.id ?? 0,
          label: t("full-time-shifts_remove-label", { label: removeCofOption?.label }),
          action: (worker: Worker) => {
            setActivePopover({ type: REMOVE_COF_POPOVER, data: worker });
          }
        }
      ];
    }

    // Filter out the current status
    return allStatusOptions
      .filter((status) => ((status.code !== currentCode && status.code !== "RAS")
        || (currentCode === "RAS" && !["RAS", "TRA", "RDU"].includes(status.code))))
      .map((status) => ({
        id: status.id,
        label: status.code === "SCH" ? t("full-time-shifts_status-reset", { label: status.label }) : status.label,
        action: (worker: Worker) => {
          setActivePopover({ type: status.code, data: worker });
        }
      }));
  };

  const handleSortClick = (field: string) => {
    setSortModel((prev) => {
      if (prev.field === field) {
        if (prev.direction === "asc") return { field, direction: "desc" };
        if (prev.direction === "desc") return { field, direction: "asc" };
      }
      return { field, direction: "asc" };
    });
  };

  const countColorPallet = useMemo(() => {
    let returnValue = {
      backgroundColor: "#F5F5F5",
      color: "rgba(0,0,0,0.87)"
    };
    try {
      if (availableWorkers === requiredWorkers) {
        returnValue = {
          backgroundColor: "#E8F5E9",
          color: "#388E3C"
        };
      } else if (availableWorkers < requiredWorkers) {
        returnValue = {
          backgroundColor: "#D32F2F0A",
          color: "#E44B48"
        };
      } else if (availableWorkers > requiredWorkers) {
        returnValue = {
          backgroundColor: "#FFFEFB",
          color: "#FBC02D"
        };
      }
    } catch (e) {
      return returnValue;
    }
    return returnValue;
  }, [availableWorkers, requiredWorkers]);

  useEffect(() => {
    try {
      if (getFullTimeShiftsFiltersStatus === "fulfilled" && schedulingStatuses) {
        const options = schedulingStatuses.map((status) => ({
          id: status.id,
          label: status.name,
          checked: true,
          code: status.code
        }));
        setStatusOptions(options.sort(
          (a, b) => FILTER_STATUS_ORDER.indexOf(a.code) - FILTER_STATUS_ORDER.indexOf(b.code)
        ));
      }
    } catch (e) {
      // Do nothing
    }
  }, [schedulingStatuses, getFullTimeShiftsFiltersStatus]);

  useEffect(() => {
    const lowerSearch = search.toLowerCase();
    const debouncedFilter = debounce(() => {
      let newFilteredJobs = lowerSearch === ""
        ? jobs
        : jobs.filter((job) =>
          job.searchString.includes(lowerSearch)
        );
      // Also filter by status option and worker name
      if (!statusOptions?.every((option) => option.checked)) {
        newFilteredJobs = newFilteredJobs.map((job) => {
          return {
            ...job,
            filteredWorkers: job.workers?.filter((worker) => {
              const workerStatus = worker.status.label;
              return statusOptions?.some(
                (option) => option.checked && option.label === workerStatus
              );
            })
          };
        });
      }
      setFilteredJobs(newFilteredJobs);
    }, DEBOUNCE_DELAY);
    debouncedFilter();

    return () => {
      debouncedFilter.cancel();
    };
  }, [search, jobs, statusOptions]);

  useEffect(() => {
    if (sortModel.field && sortModel.direction) {
      setJobs(prevState => {
        return [...prevState].sort((a, b) => {
          const dir = sortModel.direction === "asc" ? 1 : -1;
          switch (sortModel.field) {
            case "jobName":
              return a.jobName.localeCompare(b.jobName) * dir;
            case "demand":
              return (a.demand - b.demand) * dir;
            case "gaps": {
              const statusOrder = { OS: 0, "": 1, US: 2 };
              const aStatus = a.jobStatus || "";
              const bStatus = b.jobStatus || "";
              if (statusOrder[aStatus] !== statusOrder[bStatus]) {
                return (statusOrder[aStatus] - statusOrder[bStatus]) * dir;
              }
              if (aStatus === "OS") return (b.value - a.value) * dir; // OS: high to low
              if (aStatus === "US") return (a.value - b.value) * dir; // US: low to high
              return 0;
            }
            default:
              return 0;
          }
        });
      });
    }
  }, [sortModel]);

  const moveWorker = (movedWorker: Worker, toJobId: number) => {
    // Reset sort model when moving a worker
    setSortModel({
      field: null,
      direction: null
    });

    setJobs((prev) => {
      const newJobs = [...prev];
      const fromJobIndex = newJobs.findIndex((j) =>
        j.workers.some((w) => w.id === movedWorker.id)
      );

      // existing worker object
      const worker: Worker = fromJobIndex !== -1 ?
        newJobs[fromJobIndex]?.workers.find((w) => w.id === movedWorker.id) ?? {...movedWorker,
          originalJobId: movedWorker.primaryJobId,
          movedFromJobId: -1
        } : {...movedWorker,
          originalJobId: movedWorker.primaryJobId,
          movedFromJobId: -1
        };

      // Simply remove worker if toJobId is -1
      if (fromJobIndex !== -1 && toJobId === -1) {
        newJobs[fromJobIndex].workers = newJobs[fromJobIndex].workers.filter((w) => w.id !== worker.id);
        const updatedJob = {
          ...newJobs[fromJobIndex],
          workers: newJobs[fromJobIndex].workers.filter((w) => w.id !== worker.id)
        };
        newJobs[fromJobIndex] = recalculate(updatedJob);
        return newJobs;
      }

      const toJobIndex = newJobs.findIndex((j) => j.id === toJobId);

      if (toJobIndex === -1) return prev;

      // Prevent dropping in the same job
      if (fromJobIndex === toJobIndex) return prev;

      const fromJob = fromJobIndex !== -1 ? { ...newJobs[fromJobIndex] } : null;
      const toJob = { ...newJobs[toJobIndex] };

      if (fromJob !== null) {
        // Remove from source
        fromJob.workers = fromJob.workers.filter((w) => w.id !== worker.id);
      }
      // Add to target
      const newStatus = (worker.originalJobId === toJob.id
       && workcenterId === worker.originalWorkcenterId
      ) ?
        statusOptions.find(statusOption => statusOption.code === "SCH") ??
        {
          id: 1,
          label: "Scheduled",
          code: "SCH"
        }
        : statusOptions.find(statusOption => statusOption.code === "RAS") ?? {
        id: 3,
        label: "Reassigned",
        code: "RAS"
      };
      toJob.workers = [...toJob.workers, {
        ...worker,
        status: newStatus,
        jobId: toJob.id,
        jobName: toJob.jobName,
        originalJobId: worker.originalJobId ? worker.originalJobId : fromJob !== null ? fromJob.id : -1,
        movedFromJobId: worker.movedFromJobId ? worker.movedFromJobId : fromJob !== null ? fromJob.id : -1,
        note: worker.workcenterId !== workcenterId ?
          newStatus.code === "SCH" ? "" : t("full-time-shifts_by-name", { name: yourName })
          : worker.originalStatus?.id !== newStatus.id ?
            worker.originalJobId !== toJob.id ?
              worker.movedFromJobId ?
                worker.movedFromJobId !== toJob.id ?
                  t("full-time-shifts_by-name", { name: yourName }) : ""
                : fromJob === null || fromJob?.id !== toJob.id ?
                  t("full-time-shifts_by-name", { name: yourName }) : ""
              : ""
            : worker.originalStatus.code !== "SCH" ?
              (worker.originalStatus.code !== "RAS" || worker.movedFromJobId === toJob.id) ?
                worker.updatedBy ?
                  t("full-time-shifts_by-name", { name: worker.updatedBy }) : ""
                : t("full-time-shifts_by-name", { name: yourName })
              : "",
        moved: worker.workcenterId !== workcenterId ? true :
          worker.movedFromJobId ? (
          worker.movedFromJobId !== toJob.id
        ) : fromJob !== null ? fromJob.id !== toJob.id : true
      }];

      if (fromJob !== null) {
        newJobs[fromJobIndex] = recalculate(fromJob);
      }
      if (toJobIndex !== -1) {
        newJobs[toJobIndex] = recalculate(toJob);
      }
      return newJobs;
    });
    setLastWorkerDroppedToJobId(toJobId);
  };

  useEffect(() => {
    if (activePopover?.data?.id) {
      dispatch(fetchWorkerCallOffs(activePopover?.data?.id));
    }
  }, [activePopover?.data?.id, dispatch]);

  useEffect(() => {
    const generateLeaveRequestText = (
      leaveRequestTypeId?: number,
      leaveRequestStatusId?: number,
      leaveRequestDurationMinutes?: number
    ): string => {
      try {
        if (!leaveRequestTypeId || !leaveRequestStatusId) return "";

        const hours = Math.floor((leaveRequestDurationMinutes ?? 0) / 60);
        const minutes = (leaveRequestDurationMinutes ?? 0) % 60;


        const durationParts = [];
        if (hours > 0) durationParts.push(hours > 1 ? t("full-time-shifts_hours-other", { count: hours }) : t("full-time-shifts_hours-one", { count: hours }));
        if (minutes > 0) durationParts.push(minutes > 1 ? t("full-time-shifts_minutes-other", { count: minutes }) : t("full-time-shifts_minutes-one", { count: minutes }));
        const durationText = durationParts.length ? ` (${durationParts.join(" ")})` : "";

        const requestType = requestTypes?.find((type) => type.id === leaveRequestTypeId);
        const requestStatus = requestStatusTypes?.find((status) => status.id === leaveRequestStatusId);

        if (requestType?.code === "sc" || requestStatus?.code !== "a") {
          return "";
        }

        return `${requestType?.name ?? ""}${durationText}`;
      } catch (e) {
        return "";
      }
    };

    const mapShiftDataToJobs = (
      shiftViewDetails: {
        jobDemands: { jobId: number; demand: number }[];
        workers: {
          workerId: string;
          firstName: string;
          lastName: string;
          primaryJobId: number;
          originalPrimaryJobId: number;
          originalWorkcenterId?: number;
          workcenterId?: number;
          workerTypeId?: number;
          workerSubTypeId?: number;
          badgeNumber?: number;
          companyEmployeeId?: string;
          availabilityStatusId?: number;
          availabilityUpdatedBy?: string;
          leaveRequestTypeId?: number;
          leaveRequestStatusId?: number;
          leaveRequestDurationMinutes?: number;
          eligibleJobIds?: number[];
          isPatternChange?: boolean;
        }[];
      },
      jobsMap: Record<number, string>,
      workcentersMap: Record<number, string>
    ): Job[] => {
      const workersByJobId: Record<number, Worker[]> = {};

      for (const worker of shiftViewDetails.workers) {
        const jobId = worker.primaryJobId;
        const jobName = jobsMap?.[jobId];
        const originalJobId = worker.originalPrimaryJobId ?? jobId;
        const originalJobName = jobsMap?.[originalJobId] ?? jobsMap?.[jobId] ?? t("full-time-shifts_unknown-job");
        const workcenterId = worker.workcenterId;
        const workcenterName = workcenterId ? workcentersMap?.[workcenterId] : "";
        const originalWorkcenterId = worker.originalWorkcenterId ?? worker.originalWorkcenterId;
        const originalWorkcenterName = originalWorkcenterId ? workcentersMap?.[originalWorkcenterId] : "";
        const name = `${worker.firstName} ${worker.lastName}`.trim() || t("full-time-shifts_unnamed");
        const initials = `${worker.firstName ? worker.firstName[0] : ""}${worker.lastName ? worker.lastName[0] : ""}`.toUpperCase();

        let status: Worker["status"] = statusOptions?.find(
          (statusOption) => statusOption.code === "SCH"
        ) ?? {
          id: 1,
          label: "Scheduled",
          checked: true,
          code: "SCH"
        };
        if (worker.availabilityStatusId) status = statusOptions?.find(
          (statusOption) => statusOption.id === worker.availabilityStatusId
        ) ?? {
          id: 1,
          label: "Scheduled",
          checked: true,
          code: "SCH"
        };
        const workerTypeName = worker.workerTypeId ?
          workerTypes?.find((type) => type.id === worker.workerTypeId)?.name
          : "";
        const workerSubTypeName = worker.workerSubTypeId ?
          workerSubTypes?.find((subType) => subType.id === worker.workerSubTypeId)?.name
          : "";
        const workerTypeAndSubTypeString = `${workerTypeName}${workerSubTypeName !== "" ? ` - ${workerSubTypeName}` : ""}`.trim();
        // TODO: Did workaround as we are getting wrong originalJobId and originalJobName when worker is moved by Generate Scheduler
        const mappedWorker: Worker = {
          id: worker.workerId,
          name,
          initials,
          status,
          originalStatus: status,
          jobId,
          jobName,
          originalJobId: status.code === "SCH" ? jobId : originalJobId, // Workaround - When due to scheduler status code is SCH, we need to keep the original job id
          originalJobName: status.code === "SCH" ? jobName : originalJobName, // Workaround - When due to scheduler status code is SCH, we need to keep the original job name
          workcenterId,
          workcenterName,
          originalWorkcenterId: status.code === "SCH" ? workcenterId : originalWorkcenterId, // Workaround - When due to scheduler status code is SCH, we need to keep the original workcenter id
          originalWorkcenterName: status.code === "SCH" ? workcenterName : originalWorkcenterName, // Workaround - When due to scheduler status code is SCH, we need to keep the original workcenter name
          eligibleJobIds: worker.eligibleJobIds ?? [],
          moved: false,
          isPatternChange: worker.isPatternChange ?? false,
          info: `${worker.badgeNumber ? worker.badgeNumber
            : worker.companyEmployeeId} (${workerTypeAndSubTypeString})`,
          updatedBy: worker.availabilityUpdatedBy ?? "",
          note: status.code !== "COF" ?
            status.code !== "SCH" ?
              worker.availabilityUpdatedBy ?
                t("full-time-shifts_by-name", { name: worker.availabilityUpdatedBy }) :
                ""
              : ""
            : generateLeaveRequestText(worker.leaveRequestTypeId, worker.leaveRequestStatusId, worker.leaveRequestDurationMinutes)
        };

        if (!workersByJobId[jobId]) {
          workersByJobId[jobId] = [];
        }
        workersByJobId[jobId].push(mappedWorker);
      }

      return shiftViewDetails.jobDemands.map((jd) => {
        const allWorkers = workersByJobId[jd.jobId] || [];

        const effectiveWorkers = allWorkers.filter(
          (w) => !NON_EFFECTIVE_STATUS_CODES.includes(w.status.code)
        );

        const demand = jd.demand;
        const actualCount = effectiveWorkers.length;
        const difference = Math.abs(actualCount - demand);

        let jobStatus: Job["jobStatus"] = "";
        if (actualCount > demand) jobStatus = "OS";
        else if (actualCount < demand) jobStatus = "US";

        return {
          id: jd.jobId,
          jobName: jobsMap?.[jd.jobId] || t("full-time-shifts_unknown-job"),
          demand,
          workers: allWorkers,
          jobStatus,
          value: difference,
          searchString: generateJobSearchString(jobsMap?.[jd.jobId], allWorkers, jobStatus)
        };
      });
    };
    if (getFullTimeShiftViewDetailsStatus === "fulfilled"
      && getFullTimeShiftsFiltersStatus === "fulfilled"
      && getRequestStatusTypesStatus === "fulfilled"
    ) {
      const jobsData = mapShiftDataToJobs(
        fullTimeShiftViewDetails,
        jobsMap || {},
        workcentersMap || {}
      );
      setJobs(jobsData);
      setSortModel({ field: "jobName", direction: "asc" });
      dispatch(resetFullTimeShiftViewDetails());
    }
  }, [fullTimeShiftViewDetails, getFullTimeShiftViewDetailsStatus, getFullTimeShiftsFiltersStatus, jobsMap, workcentersMap, statusOptions, workerSubTypes, workerTypes, requestTypes, getRequestStatusTypesStatus, requestStatusTypes, dispatch, t]);

  useEffect(() => {
    if (!openDialog) return;

    const raf = requestAnimationFrame(() => {
      if (dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        setDialogSize({ width: rect.width, height: rect.height });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [openDialog]);

  useResizeObserver(dialogRef, setDialogSize);

  useEffect(() => {
    if (openDialog) {
      // Note: Do not remove any of them
      // Reset when opening the dialog
      setWorkerBeingUpdated(null);
      setActivePopover(null);
      dispatch(resetReassignWorkers());
      setPinnedWorker(null);
      setStatusOptions(prevState => {
        return prevState.map(option => ({
          ...option,
          checked: true
        }));
      });
      setJobs([]);
      setFilteredJobs([]);
      setSearch("");
      setWorkerToDrop(null);
      setWorkerToJobId(null);
      const payload = [{
        shiftId: shiftId,
        workcenterId: workcenterId
      }];
      dispatch(getFullTimeShiftsMeta(payload));
      dispatch(getFullTimeShiftViewDetails(payload[0]));
    }
  }, [dispatch, shiftId, workcenterId, openDialog]);

  useEffect(() => {
    try {
      // Handle special case of TRA - Training status change
      if (localStorage.getItem(`doNotRemindMeTrainingStatusChange-${currentUserId}`)
        && activePopover?.type === "TRA"
      ) {
        setWorkerBeingUpdated(activePopover?.data ?? null);
        dispatch(
          updateWorkerAvailabilityStatus({
            workerId: activePopover?.data?.id,
            ftShiftId: shiftId,
            fromStatusCode: activePopover?.data?.status?.code,
            toStatusCode: "TRA"
          })
        );
      }

      // Handle special case of RDU - Restricted Duty status change
      if (localStorage.getItem(`doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`)
        && activePopover?.type === "RDU"
      ) {
        setWorkerBeingUpdated(activePopover?.data ?? null);
        dispatch(
          updateWorkerAvailabilityStatus({
            workerId: activePopover?.data?.id,
            ftShiftId: shiftId,
            fromStatusCode: activePopover?.data?.status?.code,
            toStatusCode: "RDU"
          })
        );
      }
    } catch (e) {
      // Do nothing
    }
  }, [activePopover, currentUserId, dispatch, shiftId]);

  const onCloseAction = () => {
    if (jobs.find(job => job.workers.some(worker => worker.moved))) {
      confirm({
        title: t("full-time-shifts_unsaved-changes"),
        description: t("full-time-shifts_unsaved-changes-description"),
        confirmationText: t("full-time-shifts_yes"),
        cancellationText: t("full-time-shifts_no")
      }).then(() => closeDialog()).catch(() => {
        // Do nothing on cancel
      });
    } else {
      closeDialog();
    }
  };

  const isFilterApplied = statusOptions?.findIndex(
    (option) => !option.checked
  ) !== -1;

  return (
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason === "escapeKeyDown") {
            if (activePopover?.type) {
              // Just clear the type, preserve other popover state
              setActivePopover((prevState) => ({
                ...prevState,
                type: ""
              }));
              return;
            }
            if (pinnedWorker !== null || workerToDrop !== null) {
              // If a worker is pinned, unpin it
              setPinnedWorker(null);
              setWorkerToDrop(null);
              setWorkerToJobId(null);
              return;
            }
          }

          // If no popover is active, close the dialog
          onCloseAction();
        }}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: "calc(100vh - 64px)",
              maxHeight: "calc(100vh - 64px)",
              margin: "32px 0",
              display: "flex",
              flexDirection: "column"
            }
          }
        }}
      >
        {pinnedWorker && (
          <WorkerPreviewBox
            name={pinnedWorker.name}
            initials={pinnedWorker.initials}
            info={pinnedWorker.info ?? "-"}
            x={mousePosition.x}
            y={mousePosition.y}
          />
        )}
        <Box
          ref={dialogRef}
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            overflowX: "auto",
            minWidth: "1065px",
            height: "100%"
          }}
        >
          <DialogTitle
            sx={{
              padding: "24px 24px 8px 24px"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "24px",
                  fontWeight: 400,
                  lineHeight: "32px",
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_shift-details")}
              </Typography>
              {!availableWorkers
              && !requiredWorkers
              && getFullTimeShiftsMetaStatus === "pending" ? null : (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "rgba(0,0,0,0.12)",
                    backgroundColor: countColorPallet.backgroundColor,
                    borderRadius: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 12px",
                    gap: "6px"
                  }}
                >
                  <GroupOutlinedIcon
                    sx={{ fontSize: 18, color: "rgba(0,0,0,0.6)" }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: "18px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_confirmed-required", { available: availableWorkers, required: requiredWorkers })}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: "8px",
                paddingRight: "24px",
                gap: "24px"
              }}
            >
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.6)"
                  }}
                >
                  {t("full-time-shifts_location-work-center")}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.87)",
                    maxWidth: "260px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {`${locationName} - ${workcenterName}`}
                </Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.6)"
                  }}
                >
                  {t("full-time-shifts_shift-pattern")}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.87)",
                    maxWidth: "260px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {shiftPatternName}
                </Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" flexDirection="column">
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.6)"
                  }}
                >
                  {t("full-time-shifts_date")}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "rgba(0,0,0,0.87)"
                  }}
                >
                  {start} - {end}
                </Typography>
              </Box>
            </Box>
            {/* FILTER */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "16px 0px 0px 0px",
                gap: "16px"
              }}
            >
              <>
                <Badge
                  color="primary"
                  variant="dot"
                  overlap="circular"
                  invisible={!isFilterApplied}
                >
                  <Box
                    sx={{
                      flex: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 0.5,
                      borderRadius: "4px",
                      background: "#0000000A",
                      cursor: "pointer",
                      ":hover": {
                        background: "#0000001F"
                      }
                    }}
                    onClick={(e) => {
                      setFilterAnchorEl(e.currentTarget);
                    }}
                  >
                    <FilterListIcon />
                  </Box>
                </Badge>
                <CheckboxPopover
                  anchorElement={filterAnchorEl}
                  options={statusOptions}
                  setOptions={(options) =>
                    setStatusOptions((prevStates) => {
                      return prevStates.map((prevState) => {
                        const existingOption = options.find(
                          (o) => o.label === prevState.label
                        );
                        return existingOption
                          ? { ...prevState, checked: existingOption.checked }
                          : prevState;
                      });
                    })
                  }
                  onClose={() => {
                    setFilterAnchorEl(null);
                  }}
                  selectAllCheckboxOption={true}
                  showCount={false}
                  disableMaxHeight
                />
              </>
              <Box sx={{ flex: 1 }}>
                <TextField
                  hiddenLabel
                  value={search}
                  onChange={(e) => {
                    try {
                      const text = e.target.value;
                      setSearch(text);
                    } catch (error) {
                      // Do nothing
                    }
                  }}
                  placeholder={t("common_search")}
                  variant="filled"
                  size="small"
                  slotProps={{
                    input: {
                      disableUnderline: true,
                      startAdornment: (
                        <SearchIcon
                          style={{
                            height: "24px",
                            width: "24px",
                            color: "rgba(0,0,0,0.56)",
                            marginRight: "8px"
                          }}
                        />
                      ),
                      endAdornment:
                        search !== "" ? (
                          <ClearIcon
                            onClick={() => {
                              setSearch("");
                            }}
                            style={{
                              height: "24px",
                              width: "24px",
                              color: "rgba(0,0,0,0.56)",
                              cursor: "pointer"
                            }}
                          />
                        ) : (
                          <></>
                        )
                    }
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiFilledInput-root": {
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontFamily: "Roboto",
                      fontWeight: "400",
                      lineHeight: "20px",
                      letterSpacing: "0.4px",
                      color: "rgba(0,0,0,0.87)"
                    }
                  }}
                />
              </Box>
            </Box>
          </DialogTitle>
          {/* REASSIGN WORKER HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "4px 24px",
              height: "48px"
            }}
          >
            <Box
              onClick={() => handleSortClick("jobName")}
              sx={{
                width: "20%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                cursor: "pointer"
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_job-role")}
              </Typography>
              {sortModel.field === "jobName" && (
                <IconButton>{renderSortIcon("jobName")}</IconButton>
              )}
            </Box>
            <Box
              onClick={() => handleSortClick("demand")}
              sx={{
                width: "10%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer"
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_required")}
              </Typography>
              {sortModel.field === "demand" && (
                <IconButton>{renderSortIcon("demand")}</IconButton>
              )}
            </Box>
            <Box
              onClick={() => handleSortClick("gaps")}
              sx={{
                width: "15%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                cursor: "pointer"
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_gaps")}
              </Typography>
              {sortModel.field === "gaps" && (
                <IconButton>{renderSortIcon("gaps")}</IconButton>
              )}
            </Box>
            <Box
              sx={{
                width: "35%",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                cursor: "default"
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_workers")}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "20%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                cursor: "default"
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "rgba(0,0,0,0.87)"
                }}
              >
                {t("full-time-shifts_status")}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{
            margin: "0px 24px"
          }}/>
          <DialogContent
            ref={jobContainerRef}
            sx={{
              paddingLeft: "24px",
              paddingRight: "24px",
              overflowY: "auto",
              flex: 1
            }}
          >
            <>
              {getFullTimeShiftViewDetailsStatus === "pending" ||
              getFullTimeShiftsFiltersStatus === "pending" ? (
                // Show 4–5 skeleton rows
                <Box
                  sx={{
                    height: "100%",
                    marginTop: "-22px"
                  }}
                >
                  {[...Array(5)].map((_, index) => (
                    <JobRowSkeleton key={index} />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    marginTop: "-22px"
                  }}
                >
                  {filteredJobs.map((job, index) => (
                    <JobRow
                      key={`${job.id}-${index}`}
                      ref={(node) => setJobRef(job.id, node)}
                      jobId={job.id}
                      shiftId={shiftId}
                      jobName={job.jobName}
                      workers={
                        statusOptions?.findIndex(
                          (option) => !option.checked
                        ) !== -1
                          ? job.filteredWorkers ?? []
                          : job.workers
                      }
                      moveWorker={moveWorker}
                      jobStatus={job.jobStatus}
                      demand={job.demand}
                      value={job.value}
                      search={search}
                      setPinnedWorker={setPinnedWorker}
                      pinnedWorker={pinnedWorker}
                      handlePinWorker={handlePinWorker}
                      hasPermissionToReassignWorkers={
                        hasPermissionToReassignWorkers
                      }
                      getAvailableStatusOptions={getAvailableStatusOptions}
                      workerToDrop={workerToDrop}
                      setWorkerToDrop={setWorkerToDrop}
                      setWorkerToJobId={setWorkerToJobId}
                      lastMovedWorker={lastMovedWorker}
                      setLastMovedWorker={setLastMovedWorker}
                      dropdownContainerRef={jobContainerRef}
                      workcenterId={workcenterId}
                    />
                  ))}
                </Box>
              )}
            </>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                paddingTop: "8px",
                paddingBottom: "8px",
                padding: "16px"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#eee",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  color: "rgba(0,0,0,0.87)",
                  width: "fit-content",
                  pl: "4px",
                }}
              >
                <DragIndicatorIcon
                  sx={{ fontSize: 16, color: "rgba(0,0,0,0.54)", mr: 0.5 }}
                />
                {t("full-time-shifts_drag-drop-instruction")}
              </Box>
              <Box
                sx={{
                  width: "30%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button
                  onClick={() => {
                    onCloseAction();
                  }}
                  variant="text"
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px"
                  }}
                >
                  {t("common_cancel")}
                </Button>
                <LoadingButton
                  loadingPosition="start"
                  onClick={() => {
                    const payload = jobs.flatMap((job) =>
                      job.workers
                        .filter((worker) => worker.moved)
                        .map((worker) => ({
                          ftShiftId: shiftId,
                          workerId: worker.id,
                          jobId: job.id,
                          workcenterId: workcenterId,
                          locationId: locationId,
                          originalPrimaryJobId: worker.originalJobId
                        }))
                    );
                    dispatch(reassignWorkers(payload));
                  }}
                  disabled={
                    !hasPermissionToReassignWorkers ||
                    reassignWorkersStatus === "fulfilled" ||
                    reassignWorkersStatus === "pending" ||
                    getFullTimeShiftViewDetailsStatus === "pending" ||
                    getFullTimeShiftsFiltersStatus === "pending" ||
                    !jobs.find((job) =>
                      job.workers.find((worker) => worker.moved)
                    )
                  }
                  variant="contained"
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "14px",
                    color: "#fff"
                  }}
                  loading={reassignWorkersStatus === "pending"}
                >
                  {reassignWorkersStatus === "pending" ? t("full-time-shifts_saving") : t("common_save")}
                </LoadingButton>
              </Box>
            </Box>
          </DialogActions>
        </Box>
        {reassignWorkersStatus === "error" && (
          <Snackbar
            open={reassignWorkersStatus === "error"}
            autoHideDuration={2000}
            onClose={() => dispatch(resetReassignWorkers())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="error">
              {reassignWorkersErrorMessage}
            </MuiAlert>
          </Snackbar>
        )}
        {reassignWorkersStatus === "fulfilled" && (
          <Snackbar
            open={reassignWorkersStatus === "fulfilled"}
            autoHideDuration={1000}
            onClose={() => {
              dispatch(resetReassignWorkers());
              closeDialog();
              const workersMoved = jobs.flatMap((job) =>
                job.workers
                  .filter((worker) => worker.moved)
                  .map((worker) => ({
                    ftShiftId: shiftId,
                    workerId: worker.id,
                    jobId: job.id,
                    workcenterId: workcenterId,
                    locationId: locationId,
                    originalPrimaryJobId: worker.originalJobId
                  }))
              );
              // Extract all different workcenters, if worker.workecenterId is different from workcenterId
              const outerWorkcentersIds = workersMoved.flatMap((payload) => {
                  return payload.workcenterId !== workcenterId
                    ? [payload.workcenterId]
                    : [];
                }
              );
              const payload = [
                {
                  shiftId: shiftId,
                  workcenterId: workcenterId
                }
              ];
              // Append all different workcenters to payload with same shiftId
              outerWorkcentersIds.forEach((workcenterId) => {
                payload.push({ shiftId: shiftId, workcenterId: workcenterId });
              });
              dispatch(getFullTimeShiftsMeta(payload));
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("full-time-shifts_reassigned")}
            </MuiAlert>
          </Snackbar>
        )}
        {updateWorkerAvailabilityAPIStatus === "error" && (
          <Snackbar
            open={updateWorkerAvailabilityAPIStatus === "error"}
            autoHideDuration={2000}
            onClose={() => dispatch(resetUpdateWorkerAvailabilityStatus())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="error">
              {updateWorkerAvailabilityAPIErrorMessage}
            </MuiAlert>
          </Snackbar>
        )}
        {updateWorkerAvailabilityAPIStatus === "fulfilled" && (
          <Snackbar
            open={updateWorkerAvailabilityAPIStatus === "fulfilled"}
            autoHideDuration={1000}
            onClose={() => {
              dispatch(resetUpdateWorkerAvailabilityStatus());
              setActivePopover((prevState) => {
                return {
                  ...prevState,
                  type: ""
                };
              });
              const payload = [
                {
                  shiftId: shiftId,
                  workcenterId: workcenterId
                }
              ];
              dispatch(getFullTimeShiftsMeta(payload));
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("full-time-shifts_status-updated-for", { name: activePopover?.data?.name })}
            </MuiAlert>
          </Snackbar>
        )}
        {getFullTimeShiftViewDetailsStatus === "error" && (
          <Snackbar
            open={getFullTimeShiftViewDetailsStatus === "error"}
            autoHideDuration={2000}
            onClose={() => dispatch(resetFullTimeShiftViewDetails())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="error">
              {getFullTimeShiftViewDetailsErrorMessage}
            </MuiAlert>
          </Snackbar>
        )}
        <>
          <Fade
            in={activePopover !== null && activePopover?.type !== ""}
            timeout={200}
            unmountOnExit
            onExited={() => {
              setActivePopover(null);
            }}
          >
            <Box
              onClick={() => {
                setWorkerBeingUpdated(null);
                setActivePopover((prevState) => {
                  return {
                    ...prevState,
                    type: ""
                  };
                });
              }}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                zIndex: 1300 // just below popover (1400)
              }}
            />
          </Fade>
          <Fade
            in={activePopover?.type === REMOVE_COF_POPOVER}
            timeout={200}
            unmountOnExit
            onExited={() => {
              setActivePopover(null);
            }}
          >
            <Box sx={{ zIndex: 1400 }}>
              <StatusChangePopover
                size="small"
                dialogHeight={dialogSize.height}
                dialogWidth={dialogSize.width}
                header={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "32px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_remove-time-off-for", { name: activePopover?.data?.name })}
                  </Typography>
                }
                content={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_worker-restored-status", { status: activePopover?.data?.originalStatus?.label })}
                  </Typography>
                }
                actions={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "8px"
                    }}
                  >
                    <Button
                      onClick={() => {
                        setWorkerBeingUpdated(null);
                        dispatch(resetUpdateWorkerAvailabilityStatus());
                        setActivePopover((prevState) => {
                          return {
                            ...prevState,
                            type: ""
                          };
                        });
                      }}
                      variant="text"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px"
                      }}
                    >
                      {t("common_cancel")}
                    </Button>
                    <LoadingButton
                      loadingPosition="start"
                      onClick={() => {
                        if (updateWorkerAvailabilityAPIStatus === "pending")
                          return;
                        setWorkerBeingUpdated(activePopover?.data ?? null);
                        dispatch(
                          updateWorkerAvailabilityStatus({
                            workerId: activePopover?.data?.id,
                            ftShiftId: shiftId,
                            fromStatusCode: activePopover?.data?.status?.code,
                            toStatusCode: "RCOF"
                          })
                        );
                      }}
                      disabled={updateWorkerAvailabilityAPIStatus === "pending"}
                      variant="contained"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        color: "#fff"
                      }}
                      loading={updateWorkerAvailabilityAPIStatus === "pending"}
                    >
                      {t("full-time-shifts_confirm-return-to-shift")}
                    </LoadingButton>
                  </Box>
                }
              />
            </Box>
          </Fade>
          <Fade
            in={activePopover?.type === "SCH"}
            timeout={200}
            unmountOnExit
            onExited={() => {
              setActivePopover(null);
            }}
          >
            <Box sx={{ zIndex: 1400 }}>
              <StatusChangePopover
                size="small"
                dialogHeight={dialogSize.height}
                dialogWidth={dialogSize.width}
                header={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "32px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_mark-as-scheduled", { name: activePopover?.data?.name })}
                  </Typography>
                }
                content={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_worker-job-reset", { jobName: activePopover?.data?.originalJobName, workcenterName: activePopover?.data?.originalWorkcenterName })}
                  </Typography>
                }
                actions={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "8px"
                    }}
                  >
                    <Button
                      onClick={() => {
                        setWorkerBeingUpdated(null);
                        dispatch(resetUpdateWorkerAvailabilityStatus());
                        setActivePopover((prevState) => {
                          return {
                            ...prevState,
                            type: ""
                          };
                        });
                      }}
                      variant="text"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px"
                      }}
                    >
                      {t("common_cancel")}
                    </Button>
                    <LoadingButton
                      loadingPosition="start"
                      onClick={() => {
                        if (updateWorkerAvailabilityAPIStatus === "pending")
                          return;
                        setWorkerBeingUpdated(activePopover?.data ?? null);
                        dispatch(
                          updateWorkerAvailabilityStatus({
                            workerId: activePopover?.data?.id,
                            ftShiftId: shiftId,
                            fromStatusCode: activePopover?.data?.status?.code,
                            toStatusCode: "SCH"
                          })
                        );
                      }}
                      disabled={updateWorkerAvailabilityAPIStatus === "pending"}
                      variant="contained"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        color: "#fff"
                      }}
                      loading={updateWorkerAvailabilityAPIStatus === "pending"}
                    >
                      {t("full-time-shifts_confirm")}
                    </LoadingButton>
                  </Box>
                }
              />
            </Box>
          </Fade>
          {localStorage.getItem(`doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`) !== "true"
            && (
            <Fade
              in={activePopover?.type === "RDU"
                && localStorage.getItem(`doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`) !== "true"}
              timeout={200}
              unmountOnExit
              onExited={() => {
                setActivePopover(null);
              }}
            >
              <Box sx={{ zIndex: 1400 }}>
                <StatusChangePopover
                  size="small"
                  dialogHeight={dialogSize.height}
                  dialogWidth={dialogSize.width}
                  header={
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "20px",
                        fontWeight: 500,
                        lineHeight: "32px",
                        color: "rgba(0,0,0,0.87)"
                      }}
                    >
                      {t("full-time-shifts_mark-as-restricted-duty", { name: activePopover?.data?.name })}
                    </Typography>
                  }
                  content={
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: "rgba(0,0,0,0.87)"
                      }}
                    >
                      {
                        t("full-time-shifts_worker-excluded-scheduling")
                      }
                    </Typography>
                  }
                  actions={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox
                          checked={doNotRemindMeRestrictedDutyStatusChange}
                        />}
                        label={
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "Roboto",
                              fontSize: "14px",
                              color: "rgba(0,0,0,0.87)"
                            }}
                          >
                            {t("full-time-shifts_dont-remind-me")}
                          </Typography>
                        }
                        onChange={(event:SyntheticEvent<Element, Event>, checked) => {
                          setDoNotRemindMeRestrictedDutyStatusChange(checked);
                        }}
                      />
                      <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        gap: "8px"
                      }}>
                        <Button
                          onClick={() => {
                            setWorkerBeingUpdated(null);
                            dispatch(resetUpdateWorkerAvailabilityStatus());
                            setActivePopover((prevState) => {
                              return {
                                ...prevState,
                                type: ""
                              };
                            });
                          }}
                          variant="text"
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "14px"
                          }}
                        >
                          {t("common_cancel")}
                        </Button>
                        <LoadingButton
                          loadingPosition="start"
                          onClick={() => {
                            if (updateWorkerAvailabilityAPIStatus === "pending")
                              return;
                            if (doNotRemindMeRestrictedDutyStatusChange) {
                              localStorage.setItem(
                                `doNotRemindMeRestrictedDutyStatusChange-${currentUserId}`,
                                "true"
                              );
                            }
                            setWorkerBeingUpdated(activePopover?.data ?? null);
                            dispatch(
                              updateWorkerAvailabilityStatus({
                                workerId: activePopover?.data?.id,
                                ftShiftId: shiftId,
                                fromStatusCode: activePopover?.data?.status?.code,
                                toStatusCode: "RDU"
                              })
                            );
                          }}
                          disabled={updateWorkerAvailabilityAPIStatus === "pending"}
                          variant="contained"
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "14px",
                            color: "#fff"
                          }}
                          loading={updateWorkerAvailabilityAPIStatus === "pending"}
                        >
                          {t("full-time-shifts_confirm")}
                        </LoadingButton>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </Fade>
          )}
          {localStorage.getItem(`doNotRemindMeTrainingStatusChange-${currentUserId}`) !== "true"
            && (
            <Fade
              in={activePopover?.type === "TRA"
                && localStorage.getItem(`doNotRemindMeTrainingStatusChange-${currentUserId}`) !== "true"}
              timeout={200}
              unmountOnExit
              onExited={() => {
                setActivePopover(null);
              }}
            >
              <Box sx={{ zIndex: 1400 }}>
                <StatusChangePopover
                  size="small"
                  dialogHeight={dialogSize.height}
                  dialogWidth={dialogSize.width}
                  header={
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "20px",
                        fontWeight: 500,
                        lineHeight: "32px",
                        color: "rgba(0,0,0,0.87)"
                      }}
                    >
                      {t("full-time-shifts_mark-as-training", { name: activePopover?.data?.name })}
                    </Typography>
                  }
                  content={
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: "rgba(0,0,0,0.87)"
                      }}
                    >
                      {
                        t("full-time-shifts_worker-excluded-scheduling")
                      }
                    </Typography>
                  }
                  actions={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox
                          checked={doNotRemindMeTrainingStatusChange}
                        />}
                        label={
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "Roboto",
                              fontSize: "14px",
                              color: "rgba(0,0,0,0.87)"
                            }}
                          >
                            {t("full-time-shifts_dont-remind-me")}
                          </Typography>
                        }
                        onChange={(event:SyntheticEvent<Element, Event>, checked) => {
                          setDoNotRemindMeTrainingStatusChange(checked);
                        }}
                      />
                      <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        gap: "8px"
                      }}>
                        <Button
                          onClick={() => {
                            setWorkerBeingUpdated(null);
                            dispatch(resetUpdateWorkerAvailabilityStatus());
                            setActivePopover((prevState) => {
                              return {
                                ...prevState,
                                type: ""
                              };
                            });
                          }}
                          variant="text"
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "14px"
                          }}
                        >
                          {t("common_cancel")}
                        </Button>
                        <LoadingButton
                          loadingPosition="start"
                          onClick={() => {
                            if (updateWorkerAvailabilityAPIStatus === "pending")
                              return;
                            if (doNotRemindMeTrainingStatusChange) {
                              localStorage.setItem(
                                `doNotRemindMeTrainingStatusChange-${currentUserId}`,
                                "true"
                              );
                            }
                            setWorkerBeingUpdated(activePopover?.data ?? null);
                            dispatch(
                              updateWorkerAvailabilityStatus({
                                workerId: activePopover?.data?.id,
                                ftShiftId: shiftId,
                                fromStatusCode: activePopover?.data?.status?.code,
                                toStatusCode: "TRA"
                              })
                            );
                          }}
                          disabled={updateWorkerAvailabilityAPIStatus === "pending"}
                          variant="contained"
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "14px",
                            color: "#fff"
                          }}
                          loading={updateWorkerAvailabilityAPIStatus === "pending"}
                        >
                          {t("full-time-shifts_confirm")}
                        </LoadingButton>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </Fade>
          )}
          <Fade
            in={activePopover?.type === "COF"}
            timeout={200}
            unmountOnExit
            onExited={() => {
              setActivePopover(null);
            }}
          >
            <Box sx={{ zIndex: 1400 }}>
              <StatusChangePopover
                size="medium"
                dialogHeight={dialogSize.height}
                dialogWidth={dialogSize.width}
                header={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "32px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_mark-as-time-off", { name: activePopover?.data?.name })}
                  </Typography>
                }
                content={
                  <>
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: "rgba(0,0,0,0.6)"
                      }}
                    >
                      {
                        t("full-time-shifts_time-off-note")
                      }
                    </Typography>
                    {activePopover?.data?.id && (
                      <MarkCalledOff
                        workerId={activePopover?.data?.id}
                        fromStatus={activePopover?.data?.status?.code}
                        setCalledOffData={setCalledOffData}
                        shiftDetails={shiftDetails}
                      />
                    )}
                  </>
                }
                actions={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "8px"
                    }}
                  >
                    <Button
                      onClick={() => {
                        setWorkerBeingUpdated(null);
                        dispatch(resetUpdateWorkerAvailabilityStatus());
                        setActivePopover((prevState) => {
                          return {
                            ...prevState,
                            type: ""
                          };
                        });
                      }}
                      variant="text"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px"
                      }}
                    >
                      {t("common_cancel")}
                    </Button>
                    <LoadingButton
                      loadingPosition="start"
                      onClick={() => {
                        if (
                          updateWorkerAvailabilityAPIStatus === "pending" ||
                          calledOffData === null ||
                          calledOffData?.ftRequestTypeId === 0 ||
                          calledOffData?.ftEndDateTime === "" ||
                          calledOffData?.ftLeaveTypes?.length === 0
                        )
                          return;
                        setWorkerBeingUpdated(activePopover?.data ?? null);
                        const payload = {
                          workerId: activePopover?.data?.id,
                          ftShiftId: shiftId,
                          toStatusCode: "COF",
                          fromStatusCode: calledOffData.ftFromStatus,
                          reAssignParams: null,
                          callOffParams: {
                            requestTypeId: calledOffData.ftRequestTypeId,
                            leaveTypes: calledOffData.ftLeaveTypes,
                            supervisorComment:
                            calledOffData.ftSupervisorComment,
                            startDate: calledOffData.ftStartDateTime,
                            endDate: calledOffData.ftEndDateTime
                          }
                        };

                        dispatch(updateWorkerAvailabilityStatus(payload));
                      }}
                      disabled={
                        updateWorkerAvailabilityAPIStatus === "pending" ||
                        calledOffData === null ||
                        calledOffData?.ftRequestTypeId === 0 ||
                        calledOffData?.ftEndDateTime === "" ||
                        calledOffData?.ftLeaveTypes?.length === 0
                      }
                      variant="contained"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        color: "#fff"
                      }}
                      loading={updateWorkerAvailabilityAPIStatus === "pending"}
                    >
                      {t("full-time-shifts_confirm-time-off")}
                    </LoadingButton>
                  </Box>
                }
              />
            </Box>
          </Fade>
          {workerToJobId !== null && (
            <Box
              onClick={() => {
                setWorkerToJobId(null);
                setWorkerToDrop(null);
                setPinnedWorker(null);
                setLastWorkerDroppedToJobId(null);
              }}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                zIndex: 1300 // just below popover (1400)
              }}
            />
          )}
          {workerToJobId !== null && (
            <Box sx={{ zIndex: 1400 }}>
              <StatusChangePopover
                size="small"
                dialogHeight={dialogSize.height}
                dialogWidth={dialogSize.width}
                header={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "32px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_not-qualified")}
                  </Typography>
                }
                content={
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "20px",
                      color: "rgba(0,0,0,0.87)"
                    }}
                  >
                    {t("full-time-shifts_worker-not-qualified-for-job", {
                      name: workerToDrop?.name,
                      job: workerToJobId ? jobsMap?.[workerToJobId] : t("full-time-shifts_unknown-job"),
                    })}
                    <br />
                    {t("full-time-shifts_confirm-reassignment-question")}
                  </Typography>
                }
                actions={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "8px"
                    }}
                  >
                    <Button
                      onClick={() => {
                        setWorkerToDrop(null);
                        setWorkerToJobId(null);
                        setPinnedWorker(null);
                        setLastWorkerDroppedToJobId(null);
                      }}
                      variant="text"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px"
                      }}
                    >
                      {t("common_cancel")}
                    </Button>
                    <Button
                      onClick={() => {
                        if (workerToDrop && workerToJobId) {
                          setLastMovedWorker(workerToDrop);
                          moveWorker(workerToDrop, workerToJobId);
                          setWorkerToDrop(null);
                          setWorkerToJobId(null);
                          setPinnedWorker(null);
                        }
                      }}
                      variant="contained"
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        color: "#fff"
                      }}
                    >
                      {t("full-time-shifts_confirm")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          )}
        </>
      </Dialog>
  );
};
